// src/components/Sidebar.js
import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { Link, useLocation } from "react-router-dom";

const Sidebar = () => {
    const [isOpen, setIsOpen] = useState(false);
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const navItem = "block py-2.5 px-4 rounded transition hover:bg-gray-700";
    const active = "bg-blue-700 font-semibold";

    const isPro =
        user?.subscription_tier === "pro" &&
        (user?.subscription_status === "active" || user?.subscription_status === "trialing");

    const handleLogout = () => {
        logout();
        navigate("/login");
        setIsOpen(false);
    };

    const goPricing = () => {
        navigate("/pricing");
        setIsOpen(false);
    };

    const manageBilling = async () => {
        try {
            const res = await api.post("/api/stripe/create-portal-session");
            if (res.data?.url) {
                window.location.href = res.data.url;
            } else {
                throw new Error(res.data?.error || "Unknown error");
            }
        } catch (e) {
            console.error("Manage billing failed:", e?.response?.data || e.message);
            alert("Couldn't open Billing Portal. We’ll try to sync your account first.");
            try {
                await api.post("/api/billing/sync");
            } catch { }
            // Fallback: go to Pricing page where the Sync/Manage buttons exist
            navigate("/pricing?status=portal");
        }
    };


    return (
        <>
            {/* Toggle Button (mobile) */}
            <button
                className="sm:hidden fixed top-4 left-4 z-50 bg-blue-700 text-white px-3 py-2 rounded shadow"
                onClick={() => setIsOpen(!isOpen)}
            >
                {isOpen ? "✖" : "☰"}
            </button>

            {/* Sidebar */}
            <aside
                className={`fixed sm:static top-0 left-0 h-full bg-gray-800 p-4 z-40 transform transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full sm:translate-x-0'} w-64`}
            >
                <h1 className="text-2xl font-bold mb-6 text-center text-white">My Stock Picks AI</h1>

                <nav className="space-y-2">
                    <NavLink to="/" end className={({ isActive }) => isActive ? navItem + " " + active : navItem}
                        onClick={() => setIsOpen(false)}>
                        Daily Picks
                    </NavLink>
                    <NavLink to="/top-trades" className={({ isActive }) => isActive ? navItem + " " + active : navItem}
                        onClick={() => setIsOpen(false)}>
                        Top Trades Today
                    </NavLink>
                    <NavLink to="/all-nyse-picks" className={({ isActive }) => `${isActive ? navItem + " " + active : navItem}`}
                        onClick={() => setIsOpen(false)}>
                        All NYSE Picks
                    </NavLink>
                    <NavLink to="/crypto-trades" className={({ isActive }) => isActive ? navItem + " " + active : navItem}
                        onClick={() => setIsOpen(false)}>
                        Crypto Trades
                    </NavLink>
                    <NavLink to="/search" className={({ isActive }) => isActive ? navItem + " " + active : navItem}
                        onClick={() => setIsOpen(false)}>
                        Search
                    </NavLink>                  
                    <NavLink to="/chart/AAPL" className={({ isActive }) => isActive ? navItem + " " + active : navItem}
                        onClick={() => setIsOpen(false)}>
                        Live Chart
                    </NavLink>
                    <Link
                        to="/short-squeezes"
                        className={`flex items-center px-3 py-2 rounded ${location.pathname.startsWith('/short-squeezes') ? 'bg-blue-700' : 'hover:bg-gray-700'}`}
                        onClick={() => setIsOpen(false)}>
                        <span className="ml-2">Short Squeezes</span>
                    </Link>
                    <NavLink
                        to="/parabolic-breakouts"
                        className={({ isActive }) => isActive ? navItem + " " + active : navItem}
                        onClick={() => setIsOpen(false)}>
                        Parabolic Breakouts
                    </NavLink>
                    <NavLink to="/paper-live-trading" className={({ isActive }) => isActive ? navItem + " " + active : navItem}
                        onClick={() => setIsOpen(false)}>
                        Paper/Live Trading
                    </NavLink>
                    <NavLink
                        to="/bot" className={({ isActive }) => isActive ? navItem + " " + active : navItem}
                        onClick={() => setIsOpen(false)}>
                        Trading Bot
                    </NavLink>
                    <NavLink
                        to="/options-search" className={({ isActive }) => isActive ? navItem + " " + active : navItem}
                        onClick={() => setIsOpen(false)}>
                        Options (Chains)
                    </NavLink>
                    <NavLink
                        to="/dividend-tracker" className={({ isActive }) => isActive ? navItem + " " + active : navItem}
                        onClick={() => setIsOpen(false)}>
                        Dividend Tracker
                    </NavLink>
                    <NavLink
                        to="/earnings-predictor" className={({ isActive }) => isActive ? navItem + " " + active : navItem}
                        onClick={() => setIsOpen(false)}>
                        Earnings Predictor
                    </NavLink>
                    <NavLink to="/disclaimer" className={({ isActive }) => isActive ? navItem + " " + active : navItem}
                        onClick={() => setIsOpen(false)}>
                        Disclaimer
                    </NavLink>
                    <NavLink to="/how-to-use" className={({ isActive }) => isActive ? navItem + " " + active : navItem}
                        onClick={() => setIsOpen(false)}>
                        How to Use
                    </NavLink>

                    {/* Auth + Billing */}
                    <div className="border-t border-gray-700 mt-4 pt-4 space-y-2">
                        {!user ? (
                            <>
                                <NavLink to="/login" className={({ isActive }) => isActive ? navItem + " " + active : navItem}
                                    onClick={() => setIsOpen(false)}>
                                    Login
                                </NavLink>
                                <NavLink to="/register" className={({ isActive }) => isActive ? navItem + " " + active : navItem}
                                    onClick={() => setIsOpen(false)}>
                                    Register
                                </NavLink>
                            </>
                        ) : (
                            <>
                                <div className="px-2 text-sm text-gray-300">
                                    Signed in as <span className="font-semibold text-white">{user.email}</span>
                                </div>

                                {/* Badge */}
                                <div className="px-2">
                                    {isPro ? (
                                        <span className="inline-block text-xs bg-green-600/80 text-white px-2 py-1 rounded-full">PRO</span>
                                    ) : (
                                        <span className="inline-block text-xs bg-gray-600 text-white px-2 py-1 rounded-full">FREE</span>
                                    )}
                                </div>

                                {/* Billing button */}
                                {isPro ? (
                                    <button
                                        onClick={manageBilling}
                                        className="w-full text-left bg-green-600 hover:bg-green-500 px-4 py-2 rounded text-white font-semibold"
                                    >
                                        Manage Billing
                                    </button>
                                ) : (
                                    <button
                                        onClick={goPricing}
                                        className="w-full text-left bg-blue-600 hover:bg-blue-500 px-4 py-2 rounded text-white font-semibold"
                                    >
                                        ⭐ Upgrade to Pro
                                    </button>
                                )}

                                <button
                                    onClick={handleLogout}
                                    className="w-full text-left bg-red-600 hover:bg-red-500 px-4 py-2 rounded text-white font-semibold"
                                >
                                    Logout
                                </button>
                            </>
                        )}
                    </div>
                </nav>
            </aside>
        </>
    );
};

export default Sidebar;
